// components/messages/MessageNotificationCard.jsx
import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  IconButton,
  Chip,
  Stack,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Reply as ReplyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTheme }  from '@mui/material/styles';

const MessageNotificationCard = ({ 
  message: conversation,  // Renamed to clarify this is a conversation object
  currentUser,
  onMessageClick, 
  onMarkAsRead, 
  onStar, 
  onArchive,
  onDelete,
  onReply
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const theme = useTheme();
  
  // Extract the actual message from the conversation
  const message = conversation.last_message || conversation;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleMarkAsReadClick = (event) => {
    event.stopPropagation();
    if (onMarkAsRead) onMarkAsRead(conversation);
    handleMenuClose();
  };

  const handleStarClick = (event) => {
    event.stopPropagation();
    if (onStar) onStar(conversation);
    handleMenuClose();
  };

  const handleArchiveClick = (event) => {
    event.stopPropagation();
    if (onArchive) onArchive(conversation);
    handleMenuClose();
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    if (onDelete) onDelete(conversation);
    handleMenuClose();
  };

  const handleReplyClick = (event) => {
    event.stopPropagation();
    if (onReply) onReply(conversation);
    handleMenuClose();
  };

  const handleCardClick = (event) => {
    // Don't trigger if clicking on action buttons or menu
    if (event.target.closest('button') || event.target.closest('[role="menuitem"]')) {
      return;
    }
    if (onMessageClick) onMessageClick(conversation);
  };

  const isUnread = conversation.unread_count > 0 || (!message.is_read && !message.read);
  const isGroup = message.recipients?.length > 2 || message.is_group_message;
  const isStarred = message.is_starred_by_sender || message.is_starred_by_recipient;
  
  // Get display actor based on conversation structure
  let displayActor, displayName, displayAvatar, actorTypeLabel, showGroupIcon;
  
  if (conversation.conversation_type === 'user_to_consortium') {
    // For consortium conversations, always show the consortium
    const consortiumActor = (conversation.recipient_actor?.actor_type === 'consortium' || conversation.recipient_actor?.actor_type === 'organization')
      ? conversation.recipient_actor
      : conversation.sender_actor;
    displayActor = consortiumActor;
    displayName = consortiumActor?.display_name || 'Consortium';
    displayAvatar = consortiumActor?.avatar_url;
    actorTypeLabel = consortiumActor?.actor_type;
    showGroupIcon = true;
  } else if (conversation.sender_actor && conversation.recipient_actor && currentUser) {
    // For user-to-user conversations, show the OTHER person (not current user)
    const otherActor = conversation.sender_actor?.entity_id === currentUser.id 
      ? conversation.recipient_actor 
      : conversation.sender_actor;
    displayActor = otherActor;
    displayName = otherActor?.display_name || 'User';
    displayAvatar = otherActor?.avatar_url;
    actorTypeLabel = otherActor?.actor_type;
    showGroupIcon = false;
  } else {
    // Fallback to old format
    const senderActor = message.sender_actor || message.sender;
    const partnerActor = message.partner_actor || message.partner || senderActor;
    displayActor = partnerActor;
    displayName = partnerActor?.display_name || partnerActor?.name || partnerActor?.username || 'Unknown User';
    displayAvatar = partnerActor?.avatar_url || partnerActor?.avatar;
    actorTypeLabel = partnerActor?.actor_type;
    showGroupIcon = message?.is_consortium_message || message?.is_organization_message;
  }

  // Get message preview
  const getMessagePreview = () => {

    // console.log('MessageNotificationCard message:', message);

    if (message.preview) return message.preview;
    if (message.body_preview) {
      return message.body_preview;
      // return message.body_preview.length > 100 
      //   ? `${message.body_preview.substring(0, 100)}...` 
      //   : message.body;
    }
    if (message.content) {
      return message.content.length > 100 
        ? `${message.content.substring(0, 100)}...` 
        : message.content;
    }
    return 'New message';
  };

  // Get time display
  const getTimeDisplay = () => {
    if (!message.created_at && !message.timestamp && !message.created) return '';
    
    const timestamp = message.created_at || message.timestamp || message.created;
    const date = new Date(timestamp);
    
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return format(date, 'HH:mm');
    } else if (diffInHours < 168) { // 7 days
      return format(date, 'EEE');
    } else {
      return format(date, 'MMM d');
    }
  };

  // Get participants for group messages
  const getParticipantsPreview = () => {
    if (!isGroup) return null;
    
    const participants = message.participants || message.recipients || [];
    if (participants.length === 0) return 'Group conversation';
    
    const otherParticipants = participants
      .filter(p => p.id !== displayActor?.id)
      .slice(0, 3);
    
    if (otherParticipants.length === 0) return 'Group conversation';
    
    const names = otherParticipants
      .map(p => p.name || p.username || 'User')
      .join(', ');
    
    if (otherParticipants.length > 3) {
      return `${names} and ${participants.length - 4} more`;
    }
    return names;
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        p: 2,
        py: 1,
        borderRadius: 1,
        // mb: 1.5,
        bgcolor:  'transparent',
        borderLeft: isUnread ? '2px solid' : 'none',
        borderLeftColor: 'primary.ulight',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isUnread ? 'action.selectedHover' : 'action.hover',
          transform: 'translateY(-1px)',
          boxShadow: 1
        }
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* Avatar Section */}
        <Box sx={{ position: 'relative' }}>
          {isGroup ? (
            <AvatarGroup max={3} sx={{ width: 48, height: 48 }}>
              <Avatar sx={{ width: 32, height: 32,
                           backgroundColor: theme.palette.primary.avatar,
                           color: 'white',
              }}>
                <GroupIcon />
              </Avatar>
              {message.recipients?.slice(0, 2).map((recipient, index) => (
                <Avatar 
                  key={index}
                  src={recipient.avatar}
                  sx={{ width: 32, height: 32,   
                    backgroundColor: theme.palette.primary.avatar,
                    color: 'white', }}
                >
                  {recipient.name?.[0]?.toUpperCase() || 'U'}
                </Avatar>
              ))}
            </AvatarGroup>
          ) : (
            // Single participant avatar
            <Avatar
              src={showGroupIcon ? null : displayAvatar}
              sx={{ 
                width: 32, 
                height: 32,
                bgcolor: showGroupIcon ? 'primary.main' : (isUnread ? 'primary.light' : theme.palette.primary.avatar),
              }}
            >
              {showGroupIcon ? <GroupIcon fontSize="small" /> : displayName.charAt(0).toUpperCase()}
            </Avatar>
          )}
          
          {/* {isUnread && (
            <CircleIcon 
              color="secondary" 
              sx={{ 
                position: 'absolute',
                top: -4,
                left: -4,
                fontSize: 12,
                bgcolor: 'white',
                borderRadius: '50%'
              }}
            />
          )} */}
        </Box>

        {/* Message Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                fontWeight={isUnread ? 600 : 500}
                sx={{ 
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {isGroup ? getParticipantsPreview() : displayName}
                {isGroup && (
                  <Chip
                    label="Group"
                    size="small"
                    sx={{ 
                      ml: 1, 
                      height: 18, 
                      fontSize: '0.9rem',
                      bgcolor: 'secondary.light',
                      color: 'secondary.contrastText'
                    }}
                  />
                )}
                {showGroupIcon && actorTypeLabel && (
                  <Chip
                    icon={<GroupIcon />}
                    label={actorTypeLabel}
                    size="small"
                    sx={{ 
                      ml: 1, 
                      height: 18, 
                      fontSize: '0.9rem',
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      '& .MuiChip-icon': { fontSize: '0.7rem' }
                    }}
                  />
                )}
              </Typography>
              
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  mb: 0.5,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: isUnread ? 500 : 400
                }}
              >
                {getMessagePreview()}
              </Typography>
            </Box>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.disabled">
                {getTimeDisplay()}
              </Typography>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{ 
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Tags/Status */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
            {isStarred && (
              <StarIcon 
                fontSize="small" 
                sx={{ fontSize: 14, color: 'warning.main' }} 
              />
            )}
            {message.urgent && (
              <Chip
                label="Urgent"
                size="small"
                sx={{ 
                  height: 18, 
                  fontSize: '0.9rem',
                  bgcolor: 'error.light',
                  color: 'error.contrastText'
                }}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleMarkAsReadClick}>
          <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
          {isUnread ? 'Mark as read' : 'Mark as unread'}
        </MenuItem>
        <MenuItem onClick={handleStarClick}>
          {isStarred ? (
            <>
              <StarIcon fontSize="small" sx={{ mr: 1 }} />
              Unstar
            </>
          ) : (
            <>
              <StarBorderIcon fontSize="small" sx={{ mr: 1 }} />
              Star
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleReplyClick}>
          <ReplyIcon fontSize="small" sx={{ mr: 1 }} />
          Reply
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleArchiveClick}>
          <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MessageNotificationCard;